import { FC, useEffect, useState } from 'react';
import { ArrowLeft, FolderOpen } from 'lucide-react';
import { ParcImmobilierMinimalDto, RealEstateDto } from 'api/real-estate';
import { realEstateAPI } from 'services/swagger-api/realEstateAPI';
import { Loading } from 'components';
import { formatEuroPrice } from 'features/subscription-process/home-insurance/utils/realEstateFormatters';
import { BiensTable } from './BiensTable';

interface ParcDetailProps {
  parc: ParcImmobilierMinimalDto;
  onBack: () => void;
  onSelectBien: (bien: RealEstateDto) => void;
  /** Fonction de traduction (labels REAL_ESTATE.*) */
  t: (key: string) => string;
}

/**
 * Détail d'un parc immobilier : synthèse (nb biens, surface, loyers) + liste
 * des biens du parc (GET /parcs-immobiliers/{parcId}), même design que la
 * liste de sélection de bien du tunnel de souscription.
 */
export const ParcDetail: FC<ParcDetailProps> = ({ parc, onBack, onSelectBien, t }) => {
  const [biens, setBiens] = useState<RealEstateDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    realEstateAPI
      .getParcById({ parcId: parc.id })
      .then(response => {
        if (!cancelled) setBiens(response.realEstatePark?.realEstates ?? []);
      })
      .catch(err => {
        console.error('Erreur lors de la récupération du détail du parc:', err);
        if (!cancelled) setError('Impossible de charger le détail du parc.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [parc.id]);

  return (
    <div className="space-y-4">
      {/* En-tête du parc */}
      <div className="bg-white rounded-xl p-4 shadow-sm border">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:text-primary hover:border-primary/40 transition-colors shrink-0"
            aria-label="Retour aux parcs"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <FolderOpen className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-bold text-gray-900 truncate">{parc.name}</h3>
            <span className="text-xs text-gray-500">Référence {parc.reference}</span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-4">
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-gray-800">{parc.realEstatesCount}</div>
            <div className="text-xs text-gray-600 mt-1">
              {parc.realEstatesCount > 1 ? 'Biens' : 'Bien'}
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-gray-800">
              {parc.totalArea != null ? `${parc.totalArea} m²` : '—'}
            </div>
            <div className="text-xs text-gray-600 mt-1">Surface totale</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-gray-800 truncate">
              {parc.totalRent != null ? formatEuroPrice(parc.totalRent) : '—'}
            </div>
            <div className="text-xs text-gray-600 mt-1">Loyers/mois</div>
          </div>
        </div>
      </div>

      {/* Biens du parc */}
      {loading ? (
        <div className="bg-white rounded-xl p-6 shadow-sm border min-h-40 relative">
          <Loading loading />
        </div>
      ) : error ? (
        <div className="bg-white rounded-xl p-6 shadow-sm border">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      ) : biens.length === 0 ? (
        <div className="bg-white rounded-xl p-8 shadow-sm border text-center">
          <p className="text-sm text-gray-500">Ce parc ne contient aucun bien.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <BiensTable biens={biens} onSelectBien={onSelectBien} t={t} />
        </div>
      )}
    </div>
  );
};
